import { COUNCILS, type CouncilConfig } from '../councils';
import { communityAreas, extractOutcode } from './areaDefinitions';

export const communityCouncils = Object.values(COUNCILS)
  .sort((a, b) => a.displayName.localeCompare(b.displayName));

export function searchCommunityAreas(query: string) {
  const search = query.trim().toLowerCase();
  if (!search) return communityAreas;
  const outcode = extractOutcode(search) ?? (/^[a-z]{1,2}\d[a-z\d]?$/i.test(search) ? search.toUpperCase() : null);
  const exact = communityAreas.filter((area) => area.name.toLowerCase() === search || area.slug === search.replace(/\s+/g, '-'));
  if (exact.length) return exact;
  return communityAreas.filter((area) => outcode
    ? area.postcodes.includes(outcode)
    : [area.name, area.slug.replace(/-/g, ' '), area.wardArea]
      .some((label) => label.toLowerCase().includes(search)));
}

export function searchCommunityCouncils(query: string) {
  const search = query.trim().toLowerCase();
  return communityCouncils.filter((council) => !search || [
    council.displayName, council.officialName, council.slug.replace(/-/g, ' '),
  ].some((label) => label.toLowerCase().includes(search)));
}

export function isFullPostcode(query: string) {
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(query.trim());
}

export interface CommunityPostcodeLocation {
  postcode: string;
  latitude: number;
  longitude: number;
  council: CouncilConfig | null;
}

export async function lookupCommunityPostcode(query: string, signal: AbortSignal): Promise<CommunityPostcodeLocation> {
  const postcode = query.trim().replace(/\s+/g, '').toUpperCase();
  if (!isFullPostcode(postcode)) throw new Error('Enter a full postcode, such as EH6 6QQ.');
  const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`, { signal });
  if (response.status === 404) throw new Error('Postcode not found. Check it and try again.');
  if (!response.ok) throw new Error('Postcode search is temporarily unavailable. You can still browse by council below.');
  const { result } = await response.json();
  if (result?.country !== 'Scotland') throw new Error('CityScope covers Scotland. Enter a Scottish postcode.');
  if (typeof result.latitude !== 'number' || !Number.isFinite(result.latitude)
    || typeof result.longitude !== 'number' || !Number.isFinite(result.longitude)
    || result.latitude < 54 || result.latitude > 61.5 || result.longitude < -9 || result.longitude > 0) {
    throw new Error('We could not confirm this postcode location. Please try another postcode.');
  }
  // Official authority codes avoid false matches from similar place names or overlapping bounding boxes.
  const council = communityCouncils.find((entry) => entry.authorityCode === result.codes?.admin_district) ?? null;
  return { postcode: result.postcode || postcode, latitude: result.latitude, longitude: result.longitude, council };
}
